import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { canSearchPatientLookup, canSearchPatientUhid } from '../../lib/patient-uhid-search';
import { ApiError } from '../../lib/api-client';
import {
  addIpdBillReceipt,
  addIpdBillRefund,
  cancelIpdDaycareAdmission,
  cancelIpdBill,
  createIpdBedTransfer,
  createIpdBill,
  createIpdDaycareAdmission,
  fetchIpdBedTransfers,
  fetchIpdBill,
  fetchIpdBills,
  fetchIpdDaycareAdmission,
  fetchIpdDaycareAdmissions,
  fetchIpdDaycareLookups,
  fetchIpdDischargeSummary,
  fetchIpdPatientCreditBills,
  fetchIpdPrescription,
  finalizeIpdDischarge,
  saveIpdDischargeSummary,
  saveIpdPrescription,
  updateIpdBill,
  updateIpdBillReceipt,
  updateIpdDaycareAdmission,
} from './ipd-daycare.api';
import type { CreateIpdBedTransferPayload, CreateIpdBillPayload, IpdBillPaymentInput, IpdDaycareListFilters, UpdateIpdBillPayload, UpdateIpdBillReceiptPayload, UpdateIpdDaycareAdmissionPayload, UpsertIpdDischargeSummaryPayload, UpsertIpdPrescriptionPayload } from './types';

export const useIpdDaycareAdmissions = (filters: IpdDaycareListFilters) => useQuery({ queryKey: ['ipd-daycare', 'admissions', filters], queryFn: () => fetchIpdDaycareAdmissions(filters) });
export const useIpdDaycareAdmission = (id?: string) => useQuery({ queryKey: ['ipd-daycare', 'admission', id], queryFn: () => fetchIpdDaycareAdmission(id!), enabled: Boolean(id) });
export const useIpdDaycareLookup = (type: string, search = '', extra: { state?: string; ward?: string; requisitionSection?: string } = {}) => {
  const patientLookup = type === 'patients' || type.startsWith('patients-');
  const bedLookupWithoutWard = type === 'beds' && !extra.ward;
  const alwaysFresh = patientLookup || ['referrals', 'providers', 'market-by', 'wards', 'beds', 'packages'].includes(type);
  return useQuery({
    queryKey: ['ipd-daycare', 'lookup', type, search, extra],
    queryFn: () => fetchIpdDaycareLookups(type, search, extra),
    enabled: !bedLookupWithoutWard && (!patientLookup || (type === 'patients' ? canSearchPatientUhid(search) : canSearchPatientLookup(search))),
    ...(alwaysFresh
      ? { staleTime: 0, refetchOnMount: 'always' as const, refetchOnWindowFocus: true }
      : { staleTime: 60_000 }),
  });
};

export const useCreateIpdDaycareAdmission = () => {
  const client = useQueryClient();
  return useMutation({ mutationFn: createIpdDaycareAdmission, onSuccess: () => void client.invalidateQueries({ queryKey: ['ipd-daycare'] }) });
};

export const useUpdateIpdDaycareAdmission = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateIpdDaycareAdmissionPayload }) => updateIpdDaycareAdmission(id, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['ipd-daycare'] }),
  });
};

export const useCancelIpdDaycareAdmission = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cancelIpdDaycareAdmission(id),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['ipd-daycare'] }),
  });
};

export const useIpdDischargeSummary = (admissionId?: string) => useQuery({
  queryKey: ['ipd-daycare', 'discharge-summary', admissionId],
  queryFn: () => fetchIpdDischargeSummary(admissionId!),
  enabled: Boolean(admissionId),
  retry: (failureCount, error) => error instanceof ApiError && error.status === 404 ? false : failureCount < 2,
});

export const useSaveIpdDischargeSummary = (admissionId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpsertIpdDischargeSummaryPayload) => saveIpdDischargeSummary(admissionId, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['ipd-daycare'] }),
  });
};

export const useFinalizeIpdDischarge = (admissionId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => finalizeIpdDischarge(admissionId),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['ipd-daycare'] }),
  });
};

export const useIpdBedTransfers = (admissionId?: string) => useQuery({ queryKey: ['ipd-daycare', 'bed-transfers', admissionId], queryFn: () => fetchIpdBedTransfers(admissionId!), enabled: Boolean(admissionId) });

export const useCreateIpdBedTransfer = (admissionId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateIpdBedTransferPayload) => createIpdBedTransfer(admissionId, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['ipd-daycare'] }),
  });
};

export const useIpdPrescription = (admissionId?: string) => useQuery({
  queryKey: ['ipd-daycare', 'prescription', admissionId],
  queryFn: () => fetchIpdPrescription(admissionId!),
  enabled: Boolean(admissionId),
  retry: (failureCount, error) => error instanceof ApiError && error.status === 404 ? false : failureCount < 2,
});

export const useSaveIpdPrescription = (admissionId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpsertIpdPrescriptionPayload) => saveIpdPrescription(admissionId, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['ipd-daycare'] }),
  });
};

export const useIpdBills = (admissionId?: string, sortBy: string | null = null, sortOrder: 'asc' | 'desc' = 'asc') => useQuery({
  queryKey: ['ipd-daycare', 'bills', admissionId, sortBy, sortOrder],
  queryFn: () => fetchIpdBills(admissionId!, sortBy, sortOrder),
  enabled: Boolean(admissionId),
});
export const useIpdBill = (billId?: string) => useQuery({ queryKey: ['ipd-daycare', 'bill', billId], queryFn: () => fetchIpdBill(billId!), enabled: Boolean(billId) });
export const useIpdPatientCreditBills = (patientId?: string) => useQuery({ queryKey: ['ipd-daycare', 'credit-bills', patientId], queryFn: () => fetchIpdPatientCreditBills(patientId!), enabled: Boolean(patientId) });

export const useCreateIpdBill = (admissionId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateIpdBillPayload) => createIpdBill(admissionId, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['ipd-daycare'] }),
  });
};

export const useUpdateIpdBill = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, payload }: { billId: string; payload: UpdateIpdBillPayload }) => updateIpdBill(billId, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['ipd-daycare'] }),
  });
};

export const useCancelIpdBill = () => {
  const client = useQueryClient();
  return useMutation({ mutationFn: (billId: string) => cancelIpdBill(billId), onSuccess: () => void client.invalidateQueries({ queryKey: ['ipd-daycare'] }) });
};

export const useAddIpdBillReceipt = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, payload }: { billId: string; payload: IpdBillPaymentInput }) => addIpdBillReceipt(billId, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['ipd-daycare'] }),
  });
};

export const useUpdateIpdBillReceipt = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, paymentId, payload }: { billId: string; paymentId: string; payload: UpdateIpdBillReceiptPayload }) =>
      updateIpdBillReceipt(billId, paymentId, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['ipd-daycare'] }),
  });
};

export const useAddIpdBillRefund = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, amount, mode, bankName }: { billId: string; amount: number; mode?: IpdBillPaymentInput['mode']; bankName?: string | null }) =>
      addIpdBillRefund(billId, amount, mode, bankName),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['ipd-daycare'] }),
  });
};
